<template>
    <div>       
        <v-card
            class="mx-auto"
            width="380"
            elevation="0"
            :loading="props.rewardLoading"
        >
        <template v-slot:loader="{ isActive  }">
            <v-progress-linear
                :active="isActive"
                color="deep-purple"
                height="4"
                indeterminate
            ></v-progress-linear>
        </template>

            <v-card-title>
                {{ props.title }}
            </v-card-title>

            <v-card-subtitle>
                <p>
                    <template v-for="(line, index) in subTitleLines" :key="index">
                        <v-icon size="xx-small" :color=chartOptions.colors[index]>mdi-square</v-icon> {{ line }}<br/>
                    </template>
                </p>
                
            </v-card-subtitle>

            <v-spacer></v-spacer>

            <v-chip size="small" 
                color="light-gray-darken-1" 
                variant="outlined"
                style="top:50%; transform: translate(10%, 50%);">
                Total Eligible Units: {{ props.totalUnits > 0 ? props.totalUnits : 0 }}
            </v-chip>

            <v-chip size="small" 
                variant="outlined" 
                :color="thresholdAchievedColor"
                style="top:50%; transform: translate(15%, 50%);">
                <b>Unit Count Towards Threshold: {{ props.units > 0 ? props.units : 0 }}</b>
            </v-chip>                

            <v-spacer></v-spacer>

            <div id="chart" style="position: relative;">

                <div style="position: absolute; top:45%; left:20%; transform: translate(-50%, -50%); font-size: x-small; z-index:1000">
                    <v-chip size="x-small" :color="thresholdAchievedColor" variant="outlined" style="top:50%; left:50%; transform: translate(-50%, 50%);">
                    <b>&nbsp;{{ thresholdAchieved }}&nbsp;</b> 
                    </v-chip>
                </div>
                <div style="position: absolute; top:37%; left:30%; transform: translate(-50%, -50%); font-size: x-small;">
                    <v-chip size="x-small" :color="thresholdNextColor" variant="outlined" style="top:50%; left:50%; transform: translate(-50%, 50%);">
                    <b>&nbsp;{{ unitCountToNextThreshold }} More Eligible Units To Next Threshold&nbsp;</b> 
                    </v-chip>
                </div>

                <apexchart type="radialBar" height="420" :options="chartOptions" :series="series"></apexchart>
            </div>

        </v-card>
        
    </div>
</template>

<script setup>
import { ref, computed, onMounted,  watch } from 'vue'

const props = defineProps({
  title: { type: String, default: '' },
  subTitleLine1: { type: String, default: '' },
  subTitleLine2: { type: String, default: '' },
  subTitleLine3: { type: String, default: '' },
  subTitleLine4: { type: String, default: '' },
  subTitleLine5: { type: String, default: '' },
  labels: { type: Array, default: [] },
  units: { type: Number, default: 0 },
  totalUnits: { type: Number, default: 0 },
  limit1: { type: Number, default: 0 },
  limit2: { type: Number, default: 0 },
  limit3: { type: Number, default: 0 },
  limit4: { type: Number, default: 0},
  limit5: { type: Number, default: 0},
  rewardLoading: { type: Boolean, default: false}
})

const getLimits = () => [props.limit1, props.limit2, props.limit3, props.limit4, props.limit5].filter(limit => limit > 0)

const thresholdCount = getLimits().length
const colors = ref(['#00c3ff', '#1ab7ea', '#0084ff', '#39539E', '#005D8F'].slice(-thresholdCount))

const subTitleLines = computed(() =>
    [props.subTitleLine1, props.subTitleLine2, props.subTitleLine3, props.subTitleLine4, props.subTitleLine5]
        .slice(0, thresholdCount)
        .reverse()
)
let thresholdAchievedColor = ref('')
let thresholdNextColor = ref('')
let thresholdAchieved = ref('')
let unitCountToNextThreshold = ref(0)
 
const chartOptions = {
    chart: {
        height: 390,
        type: 'radialBar',
    },
    plotOptions: {
        radialBar: {
        offsetY: 0,
        startAngle: 0,
        endAngle: 270,
        hollow: {
            margin: 5,
            size: '30%',
            background: 'transparent',
            image: undefined,
        },
        dataLabels: {
            name: {
            show: false,
            },
            value: {
            show: false,
            }
        },
        barLabels: {
            enabled: true,
            useSeriesColors: true,
            offsetX: -8,
            fontSize: '12px',
            formatter: function(seriesName, opts) {
            return seriesName + ":  " + String(opts.w.globals.series[opts.seriesIndex]).padStart(3, ' ') + "%"
            },
        },
        }
    },
    colors: colors.value,
    labels: props.labels,
    responsive: [{
        breakpoint: 480,
        options: {
        legend: {
            show: false
        }
        }
    }]
}

let series = ref(new Array(thresholdCount).fill(0))

const reCalc = (units, limits) => {

    if (units < 0) {
        units = 0
    }

    const lastIndex = limits.length - 1
    const percentages = limits.map(limit => Math.round(units / limit * 100, 0))

    thresholdAchieved.value = 'Threshold Not Achieved'

    if (units >= 0) {
        thresholdNextColor.value = colors.value[lastIndex]
    }
    unitCountToNextThreshold.value = limits[0] - units

    percentages.forEach((percentage, index) => {
        if (percentage >= 100) {
            percentages[index] = 100
            thresholdAchievedColor.value = colors.value[lastIndex - index]
            thresholdNextColor.value = colors.value[Math.max(lastIndex - index - 1, 0)]
            thresholdAchieved.value = `Threshold ${index + 1} Achieved`
            unitCountToNextThreshold.value = index < lastIndex ? limits[index + 1] - units : 0
        }
    })

    if (unitCountToNextThreshold.value < 0) {
        unitCountToNextThreshold.value = 0
    }

    series.value = percentages.map(percentage => percentage < 0 ? 0 : percentage).reverse()
}

watch(
    () => [props.units, ...getLimits()],
    () => {
        reCalc(props.units, getLimits())
    },
    {immediate:true}
)

onMounted(() => {
})


</script>
